import { useCallback, useId, useState } from 'react'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
  makeStyles,
  shorthands,
  tokens,
  DialogOpenChangeEvent,
  DialogOpenChangeData,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'
import ProjectEconomySaveContent from './ProjectEconomySaveContent'

interface ProjectEconomySaveModalProps {
  isOpen: boolean
  onClose: () => void
}

export const ProjectEconomySaveModal = ({
  isOpen,
  onClose,
}: ProjectEconomySaveModalProps) => {
  const titleId = useId()
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const styles = useStyles()

  const handleOpenChange = useCallback(
    (_event: DialogOpenChangeEvent, data: DialogOpenChangeData) => {
      // Only allow closing if not loading
      if (!data.open && !isLoading) {
        onClose()
      }
    },
    [onClose, isLoading]
  )

  return (
    <Dialog open={isOpen} modalType="modal" onOpenChange={handleOpenChange}>
      <DialogSurface
        className={hasError ? styles.surfaceError : styles.surface}
      >
        <DialogTitle className={styles.title} id={titleId}>
          Spara projektekonomi
          <Button
            data-testid="CloseModalBtn"
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={isLoading ? undefined : onClose}
            disabled={isLoading}
            className={styles.closeButton}
          />
        </DialogTitle>
        <DialogBody>
          <DialogContent className={styles.content}>
            <ProjectEconomySaveContent
              onClose={onClose}
              onLoadingChange={setIsLoading}
              onErrorChange={setHasError}
            />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

const useStyles = makeStyles({
  surface: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    ...shorthands.borderTop('4px', 'solid', tokens.colorBrandForeground1),
    backgroundColor: tokens.colorNeutralBackground1,
    maxWidth: '600px',
    minWidth: '400px',
    maxHeight: '80vh',
  },
  surfaceError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    ...shorthands.borderTop('4px', 'solid', '#d13438'),
    backgroundColor: '#ffe5e5',
    maxWidth: '600px',
    minWidth: '400px',
    maxHeight: '80vh',
  },
  title: {
    ...shorthands.padding('12px', '12px', '14px', '24px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground1,
  },
  content: {
    ...shorthands.padding('0', '24px', '24px', '24px'),
    overflowY: 'auto',
    overflowX: 'hidden',
    '& p': { margin: '14px 0' },
    '& p:first-child': { marginTop: 0 },
    '& p:last-child': { marginBottom: 0 },
  },
  closeButton: {
    marginLeft: 'auto',
    marginTop: '4px',
    marginRight: '2px',
  },
})
